import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  Board,
  ClientMessage,
  ConnectionState,
  Pin,
  PresenceUser,
  RoomError,
  ServerMessage,
  Stroke,
  StrokePart,
} from './types'
import { roomWsUrl } from './room'

const EMPTY_BOARD: Board = { slotCount: 3, slots: [], order: [], hidden: [] }

/** `stroke:begin` だけ来て `commit` が来ないときに、インジケータを畳む時間 */
const DRAWING_TIMEOUT = 10_000

/**
 * 1つのルームに対する WebSocket 接続を管理する。
 *
 * 接続先は `/room/<roomId>` で、**ルームIDが配信範囲を決める**。
 * mapId が変わったら同じソケットのまま hello を送り直す（再接続はしない）。
 * → doc/service/master.md#プロトコル
 */
export function useRealtime(roomId: string | null, mapId: string | null, userName: string) {
  const [pins, setPins] = useState<Pin[]>([])
  const [strokes, setStrokes] = useState<Stroke[]>([])
  const [rawUsers, setUsers] = useState<PresenceUser[]>([])
  const [board, setBoardState] = useState<Board>(EMPTY_BOARD)
  const [state, setState] = useState<ConnectionState>('connecting')
  /** 再接続しても直らない切断理由。入っている間は接続を諦める */
  const [error, setError] = useState<RoomError | null>(null)
  /** いま描いている人（presence の id）。stroke:begin から組み立てる */
  const [drawingIds, setDrawingIds] = useState<string[]>([])

  const socketRef = useRef<WebSocket | null>(null)
  /** setBoard の関数形で最新値を参照するため（state だと callback が古い値を掴む） */
  const boardRef = useRef<Board>(EMPTY_BOARD)
  const retryRef = useRef(0)
  const timerRef = useRef<number | null>(null)
  /** 致命的な切断理由。onclose から参照するので ref にも持つ */
  const errorRef = useRef<RoomError | null>(null)
  /** 描画中インジケータの取り下げタイマー。presence の id → timeout */
  const drawTimersRef = useRef(new Map<string, number>())
  /** 描き始めを送ってきた人。線の id → presence の id（commit で畳むために覚える） */
  const drawOwnerRef = useRef(new Map<string, string>())
  /**
   * 自分がいま描いている線。**サーバへは送っていない。**
   * ペンを放した時点で commit として1回だけ送る（→ doc/doodle/master.md#通信）
   */
  const openRef = useRef(new Map<string, Stroke>())
  // 再接続後に最新の mapId / userName で hello を送るために ref で保持する
  const helloRef = useRef({ mapId, userName })
  helloRef.current = { mapId, userName }

  const send = useCallback((msg: ClientMessage) => {
    const ws = socketRef.current
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg))
  }, [])

  /** 描画中インジケータを立てる。10秒で自動的に畳む（commit の取りこぼし対策） */
  const markDrawing = useCallback((from: string) => {
    const timers = drawTimersRef.current
    const prev = timers.get(from)
    if (prev) clearTimeout(prev)
    setDrawingIds((ids) => (ids.includes(from) ? ids : [...ids, from]))
    timers.set(
      from,
      window.setTimeout(() => {
        timers.delete(from)
        setDrawingIds((ids) => ids.filter((x) => x !== from))
      }, DRAWING_TIMEOUT),
    )
  }, [])

  const clearDrawing = useCallback((from: string) => {
    const timers = drawTimersRef.current
    const t = timers.get(from)
    if (t) clearTimeout(t)
    timers.delete(from)
    setDrawingIds((ids) => (ids.includes(from) ? ids.filter((x) => x !== from) : ids))
  }, [])

  // ---- 接続ライフサイクル（ルームが変わらない限り1本維持する） ----
  useEffect(() => {
    if (!roomId) return
    let disposed = false
    setError(null)
    errorRef.current = null

    const connect = () => {
      if (disposed || errorRef.current) return
      setState('connecting')
      const ws = new WebSocket(roomWsUrl(roomId))
      socketRef.current = ws

      ws.onopen = () => {
        if (disposed) return
        retryRef.current = 0
        setState('open')
        const { mapId, userName } = helloRef.current
        if (mapId) ws.send(JSON.stringify({ type: 'hello', mapId, user: userName } satisfies ClientMessage))
      }

      ws.onmessage = (ev) => {
        let msg: ServerMessage
        try {
          msg = JSON.parse(ev.data as string)
        } catch {
          return
        }
        switch (msg.type) {
          case 'init':
            // 参加中のマップ宛でなければ無視（マップ切替の行き違い対策）
            if (msg.mapId !== helloRef.current.mapId) return
            setPins(msg.pins)
            // 描きかけはサーバに無い。完成した線だけが届く
            setStrokes(msg.strokes ?? [])
            break
          case 'pin:upsert':
            setPins((prev) => {
              const i = prev.findIndex((p) => p.id === msg.pin.id)
              if (i < 0) return [...prev, msg.pin]
              const next = prev.slice()
              next[i] = msg.pin
              return next
            })
            break
          case 'pin:delete':
            setPins((prev) => prev.filter((p) => p.id !== msg.id))
            break
          case 'presence':
            setUsers(msg.users)
            // 抜けた人のインジケータは残さない
            setDrawingIds((ids) => ids.filter((id) => msg.users.some((u) => u.id === id)))
            break
          case 'board': {
            // hidden を持たないサーバ（更新前のプロセス）から来ても落ちないようにする
            const board: Board = { ...msg.board, hidden: msg.board.hidden ?? [] }
            boardRef.current = board
            setBoardState(board)
            break
          }

          /* ---- 落書き ----
           * begin / commit は送信者には返ってこない（手元に同じものが既にある）。
           * erase / split / clear は全員に返る。
           */
          case 'stroke:begin':
            // 線は見えない。「描いています」だけを出す
            if (msg.mapId !== helloRef.current.mapId) break
            drawOwnerRef.current.set(msg.id, msg.from)
            markDrawing(msg.from)
            break
          case 'stroke:commit': {
            // commit には送り主が載らないので、begin で覚えた対応から畳む
            const from = drawOwnerRef.current.get(msg.stroke.id)
            if (from) {
              drawOwnerRef.current.delete(msg.stroke.id)
              clearDrawing(from)
            }
            setStrokes((prev) => (prev.some((s) => s.id === msg.stroke.id) ? prev : [...prev, msg.stroke]))
            break
          }
          case 'stroke:erase': {
            const gone = new Set(msg.ids)
            setStrokes((prev) => prev.filter((s) => !gone.has(s.id)))
            break
          }
          case 'stroke:split':
            setStrokes((prev) => {
              // split は送信者にも返る。分割した本人は手元で先に適用済みなので、
              // 断片の id も除いてから足し直さないと同じ線が二重に積まれる
              const parts = new Set(msg.parts.map((p) => p.id))
              return [...prev.filter((s) => s.id !== msg.id && !parts.has(s.id)), ...msg.parts]
            })
            break
          case 'stroke:clear':
            setStrokes([])
            break

          // ---- 再接続しても直らない切断 ----
          case 'full':
          case 'rate':
          case 'closed':
            errorRef.current = msg.type
            setError(msg.type)
            break
        }
      }

      ws.onclose = () => {
        if (disposed) return
        setState('closed')
        setUsers([])
        setDrawingIds([])
        // 満室・レート制限・上限は待っても変わらないので再接続しない
        if (errorRef.current) return
        // 指数バックオフ（最大10秒）で再接続
        const delay = Math.min(10_000, 500 * 2 ** retryRef.current)
        retryRef.current += 1
        timerRef.current = window.setTimeout(connect, delay)
      }

      ws.onerror = () => ws.close()
    }

    connect()

    return () => {
      disposed = true
      if (timerRef.current) clearTimeout(timerRef.current)
      for (const t of drawTimersRef.current.values()) clearTimeout(t)
      drawTimersRef.current.clear()
      drawOwnerRef.current.clear()
      socketRef.current?.close()
      socketRef.current = null
    }
  }, [roomId, markDrawing, clearDrawing])

  // ---- マップ切替 / 名前変更 ----
  useEffect(() => {
    if (!mapId) return
    // 描きかけを持ったまま移ると、その線は誰にも届かないまま消える。移る前に出す
    for (const stroke of openRef.current.values()) send({ type: 'stroke:commit', stroke })
    openRef.current.clear()
    setPins([])
    setStrokes([])
    setDrawingIds([])
    drawOwnerRef.current.clear()
    send({ type: 'hello', mapId, user: userName })
  }, [mapId, userName, send])

  const upsertPin = useCallback(
    (pin: Pin) => {
      // 楽観更新：サーバの broadcast を待たずに手元へ反映する
      setPins((prev) => {
        const i = prev.findIndex((p) => p.id === pin.id)
        if (i < 0) return [...prev, pin]
        const next = prev.slice()
        next[i] = pin
        return next
      })
      send({ type: 'pin:upsert', pin })
    },
    [send],
  )

  const deletePin = useCallback(
    (id: string) => {
      setPins((prev) => prev.filter((p) => p.id !== id))
      send({ type: 'pin:delete', id })
    },
    [send],
  )

  const clearPins = useCallback(() => {
    setPins([])
    send({ type: 'pin:clear' })
  }, [send])

  /* ---- 落書き ----
   * 描いている最中は**何も送らない**。手元にだけ線を伸ばし、ペンを放した時点で
   * 1本まるごと `stroke:commit` する。描き始めだけは `stroke:begin` で知らせる
   * （相手には線が見えないので、「描いています」の表示に使う）。
   * → doc/service/master.md#落書きの同期方式を変える
   */

  /** 描き始め。手元に即置き、通知だけ送る */
  const beginStroke = useCallback(
    (stroke: Stroke) => {
      openRef.current.set(stroke.id, stroke)
      setStrokes((prev) => [...prev, stroke])
      send({ type: 'stroke:begin', id: stroke.id, author: stroke.author })
    },
    [send],
  )

  /** 描いている途中の追記。**手元だけ。**送信はしない */
  const extendStroke = useCallback((id: string, pts: number[]) => {
    if (!pts.length) return
    const open = openRef.current.get(id)
    if (open) open.pts = [...open.pts, ...pts]
    setStrokes((prev) => {
      const i = prev.findIndex((s) => s.id === id)
      if (i < 0) return prev
      const next = prev.slice()
      next[i] = { ...next[i], pts: [...next[i].pts, ...pts] }
      return next
    })
  }, [])

  /** 描き終わり。完成した1本を丸ごと送る */
  const endStroke = useCallback(
    (id: string) => {
      const stroke = openRef.current.get(id)
      openRef.current.delete(id)
      if (!stroke || stroke.pts.length < 2) return
      send({ type: 'stroke:commit', stroke })
    },
    [send],
  )

  const eraseStrokes = useCallback(
    (ids: string[]) => {
      if (!ids.length) return
      const gone = new Set(ids)
      for (const id of ids) openRef.current.delete(id)
      setStrokes((prev) => prev.filter((s) => !gone.has(s.id)))
      send({ type: 'stroke:erase', ids })
    },
    [send],
  )

  /** 部分消し。元の1本を断片で置き換える（parts が空なら消滅） */
  const splitStroke = useCallback(
    (id: string, parts: StrokePart[]) => {
      openRef.current.delete(id)
      setStrokes((prev) => {
        const src = prev.find((s) => s.id === id)
        if (!src) return prev
        const rest = prev.filter((s) => s.id !== id)
        return [...rest, ...parts.map((p) => ({ ...src, id: p.id, pts: p.pts }))]
      })
      send({ type: 'stroke:split', id, parts })
    },
    [send],
  )

  const clearStrokes = useCallback(() => {
    openRef.current.clear()
    setStrokes([])
    send({ type: 'stroke:clear' })
  }, [send])

  /** ボードは丸ごと置き換え。手元に即反映してからサーバへ送る */
  const setBoard = useCallback(
    (next: Board | ((prev: Board) => Board)) => {
      // 送信は副作用なので更新関数の外で組み立てる（StrictMode の二重実行を避ける）
      const value = typeof next === 'function' ? next(boardRef.current) : next
      boardRef.current = value
      setBoardState(value)
      send({ type: 'board:set', board: value })
    },
    [send],
  )

  /** 描画中フラグはサーバから来ないので、ここで presence に混ぜて返す */
  const users = useMemo(
    () => rawUsers.map((u) => (drawingIds.includes(u.id) ? { ...u, drawing: true } : u)),
    [rawUsers, drawingIds],
  )

  return {
    pins,
    strokes,
    users,
    board,
    state,
    error,
    upsertPin,
    deletePin,
    clearPins,
    setBoard,
    beginStroke,
    extendStroke,
    endStroke,
    eraseStrokes,
    splitStroke,
    clearStrokes,
  }
}
